import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OccEndpointsService } from '@spartacus/core';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { Subscription } from 'rxjs';
import { DataLayerService } from 'src/app/shared/services/data-layer.service';
import * as _ from 'underscore';
import { CustomPdpService } from '../../../custom-pdp/custom-pdp.service';
import { ProductUtils } from '../../../shared/utils/productUtils';
import { CustomAddToCartService } from '../../custom-add-to-cart.service';
import { StockAvailableStatus } from '../../../shared/config/app.config';

@Component({
  selector: 'app-product-details',
  templateUrl: './product-details.component.html',
  styleUrls: ['./product-details.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductDetailsComponent implements OnInit, OnDestroy {
  @Input() public productDetails;
  @Input() public standardCartCode;
  @Input() public unitSection;
  description: any;
  monograph: any;
  monographLink: any;
  itemAddedQuantity: any;
  baseUrl: string;
  fallbackImage = '../../../public/i18n-assets/sanofishoppe_missing_product.png';
  userDetails: any;
  cartId: any;
  cartItems: any;
  volumePrices: any;
  prodCodeSub: Subscription;
  pdpCartItemsSub: Subscription;
  allCartItemSub: Subscription;
  cartDetailsSub: Subscription;
  createdCartSub: Subscription;
  updatedCartDetailsSub: Subscription;
  images: any;
  quantityLimitReached = signal<boolean>(false);
  protected readonly StockAvailableStatus = StockAvailableStatus;
  protected readonly ProductUtils = ProductUtils;

  constructor(
    private readonly addToCart: CustomAddToCartService,
    private readonly modalService: NgbModal,
    private readonly pdpService: CustomPdpService,
    private readonly user: UserAccountFacade,
    private readonly sanitizer: DomSanitizer,
    private readonly occEndPoint: OccEndpointsService,
    private readonly dataLayerService: DataLayerService
  ) {
  }

  ngOnInit(): void {
    this.baseUrl = this.occEndPoint.getBaseUrl({
      baseUrl: true,
      prefix: false,
      baseSite: false
    });
    this.pdpService.productCode.next(false);
    this.pdpService.getPDPCategory(this.productDetails?.product?.code);
    const imagesFormat = _.clone(
      _.findWhere(this.productDetails?.product?.images, { format: 'product' })
    );

    if (imagesFormat) {
      imagesFormat.url = this.baseUrl + imagesFormat?.url;
    }

    this.images = { PRIMARY: imagesFormat };

    this.prodCodeSub = this.pdpService.productCode$.subscribe((res) => {
      if (res) {
        this.volumePrices = res?.volumePrices;
      } else {
        this.volumePrices = [];
      }
    });

    this.description = this.productDetails?.product?.description;
    this.monograph = this.productDetails?.product?.productDocuments;
    if (this.monograph) {
      for (const link of this.monograph?.productDocument) {
        this.monographLink = this.sanitizer.bypassSecurityTrustResourceUrl(
          link.url
        );
      }
    }

    this.user.get().subscribe((res) => {
      if (res) {
        this.userDetails = res;
        if (this.userDetails) {
          this.pdpService.getCarts(this.userDetails?.userName);
        }
      }
    });

    this.pdpCartItemsSub = this.pdpService.cartItems$.subscribe((res) => {
      if (res) {
        this.cartDataMapping(res);
      }
    });

    this.allCartItemSub = this.addToCart.allCartItems$.subscribe((res) => {
      if (res) {
        this.cartDataMapping(res);
      }
    });

    this.dataLayerService.viewItemDataLayer(
      this.productDetails,
      'standard process',
      'pdp-popup'
    );
  }

  cartDataMapping(response) {
    this.cartItems = response?.carts;
    this.cartItems = this.cartItems.filter((item) => item?.saveTime === undefined);
    this.cartItems = this.cartItems[this.cartItems?.length - 1];
    this.cartId = this.cartItems?.code;
  }

  performQuantityComputation(operator, idData, quantityOfUnits, capLimit: number = undefined): void {
    this.quantityLimitReached.set(ProductUtils.alterQuantity(operator, idData, quantityOfUnits, capLimit));
  }

  updateCart(idData, popover, prod): void {
    const inputElement = document.getElementById(`prodQuantity${ idData }`) as HTMLInputElement;
    const params = [
      {
        entryNumber: this.productDetails?.entryNumber,
        quantity: inputElement.value
      }
    ];

    this.addToCart.updateCartDetails(this.standardCartCode, params, this.cartId);

    this.cartDetailsSub = this.addToCart.cartDetails$.subscribe((res: any) => {
      if (res) {
        this.successPopoverDisplay(popover);
        this.dataLayerService.addCartDataLayer(
          prod,
          'standard process',
          inputElement.value,
          'cart item details'
        );
      }
    });
  }

  close(): void {
    this.modalService.dismissAll();
  }

  addToSessionCart(idData, productCode, popover): void {
    const inputElement = document.getElementById(
      `prodQuantity${ idData }`
    ) as HTMLInputElement;
    const params = {
      product: {
        code: productCode
      },
      quantity: inputElement.value
    };
    this.itemAddedQuantity = inputElement.value;

    if (this.cartItems && this.cartItems?.length === 0) {
      this.pdpService.createCart(this.userDetails?.userName);
      this.createdCartSub = this.pdpService.createdCart$.subscribe((res) => {
        if (res) {
          this.cartId = res?.code;
          this.pdpService.getCarts(this.userDetails?.userName);
          this.updateCartDetails(params, popover);
        }
      });
    } else {
      if (this.cartId === undefined) {
        this.cartItems =
          this.cartItems && this.cartItems?.length > 0
            ? this.cartItems[this.cartItems?.length - 1]
            : [];
        this.cartId = this.cartItems?.code;
      }
      this.updateCartDetails(params, popover);
    }
  }

  updateCartDetails(params, popover) {
    this.pdpService.updateCart(
      params,
      this.userDetails?.userName,
      this.cartId,
      params.quantity
    );
    this.updatedCartDetailsSub = this.pdpService.cartDetailsUpdate$.subscribe(
      (res) => {
        if (res) {
          this.successPopoverDisplay(popover);
        }
      }
    );
  }

  successPopoverDisplay(popover) {
    const num = 2500;
    popover.toggle();
    setTimeout(() => popover.close(), num);
  }

  ngOnDestroy() {
    this.prodCodeSub?.unsubscribe();
    this.prodCodeSub?.unsubscribe();
    this.pdpCartItemsSub?.unsubscribe();
    this.allCartItemSub?.unsubscribe();
    this.cartDetailsSub?.unsubscribe();
    this.createdCartSub?.unsubscribe();
    this.updatedCartDetailsSub?.unsubscribe();
  }
}
